import {
  entryKinds,
  MoreIcon,
  NumberField,
  Pick,
  Popover,
  setOwnRule,
  type EntryKind,
  type EntryRule,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  country: string;
  /** The country's name, said in the button's label. */
  name: string;
  rule: EntryRule | null;
  /** Read the borders again once the rule is kept. */
  onChanged: () => void;
}

/**
 * One's own rule at a border, when the list and Wikipedia do not know it or say it wrong: behind
 * the row's menu at its right, changed there.
 */
export function RuleEdit({ country, name, rule, onChanged }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The menu first; the rule's form once it is asked to be changed.
  const [changing, setChanging] = useState(false);
  const [kind, setKind] = useState<EntryKind>(rule?.kind ?? 'free');
  const [days, setDays] = useState<number | null>(rule?.days ?? 30);
  const keep = (said: { kind: EntryKind; days: number } | null) => {
    setOpen(false);
    setChanging(false);
    setOwnRule(country, said).then(onChanged, onChanged);
  };
  return (
    <Popover
      open={open}
      align="end"
      onOpenChange={(next) => {
        setOpen(next);
        setChanging(false);
      }}
      label={t('docs.ownRule', { country: name })}
      trigger={(props) => (
        <button
          {...props}
          type="button"
          className="icon-button icon-button-small"
          aria-label={t('docs.ownRule', { country: name })}
          title={t('docs.ownRule', { country: name })}
        >
          <MoreIcon />
        </button>
      )}
    >
      {!changing ? (
        <div className="data-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="data-menu-item"
            onClick={() => {
              setChanging(true);
            }}
          >
            {t('plan.change')}
          </button>
          {rule?.source === 'own' && (
            <button
              type="button"
              role="menuitem"
              className="data-menu-item"
              onClick={() => {
                keep(null);
              }}
            >
              {t('docs.forgetOwn')}
            </button>
          )}
        </div>
      ) : (
        <form
          className="form rule-edit"
          onSubmit={(event) => {
            event.preventDefault();
            keep({ kind, days: days ?? 0 });
          }}
        >
          <Pick
            label={t('docs.needs')}
            options={entryKinds.map((value) => ({ value, label: t(`docs.kindName.${value}`) }))}
            value={kind}
            searchable={false}
            onChange={setKind}
          />
          <NumberField
            label={t('docs.stayDays')}
            value={days}
            onChange={setDays}
            min={0}
            max={3650}
          />
          <div className="dialog-actions">
            <button type="submit" className="button button-primary">
              {t('car.save')}
            </button>
          </div>
        </form>
      )}
    </Popover>
  );
}
